import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ScreenShareIcon from '@mui/icons-material/ScreenShare';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { motion } from 'framer-motion';

import ZaraAvatar from '../../../components/zara-avatar/zara-avatar';
import { AssessmentFeedback } from './assessment-feedback';

export const InterviewStatusPage = ({
  icon,
  title,
  description,
  showTryAgainButton,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  showTryAgainButton?: boolean;
}) => (
  <Box
    display='flex'
    alignItems='center'
    justifyContent='center'
    minHeight='100vh'
  >
    <Box textAlign='center' px={3}>
      {icon}

      <Typography variant='h4' sx={{ fontSize: '1.75rem', mt: 3, mb: 2 }}>
        {title}
      </Typography>

      <Typography variant='body1'>{description}</Typography>

      {showTryAgainButton && (
        <Button
          onClick={() => window.location.reload()}
          variant='contained'
          size='large'
          color='primary'
          sx={{ my: 4 }}
        >
          Try again
        </Button>
      )}
    </Box>
  </Box>
);

export const InterviewComplete = ({
  assessmentId,
}: {
  assessmentId: string;
}) => {
  return (
    <Box
      display='flex'
      alignItems='center'
      justifyContent='center'
      minHeight='100vh'
    >
      <Box textAlign='center' px={3}>
        <AssessmentFeedback
          assessmentId={assessmentId}
          copy={{
            title: 'Thanks for taking the interview',
            ratingLabel: 'Rate your interview experience',
            commentLabel: 'Describe your interview experience',
          }}
        />
      </Box>
    </Box>
  );
};

export const InterviewNetworkConnectionLost = () => (
  <InterviewStatusPage
    icon={
      <WifiOffIcon
        sx={{
          width: 64,
          height: 64,
          color: 'info.main',
        }}
      />
    }
    title='Network connection lost'
    description='Failed to connect to the server. Please check your internet connection and try again.'
    showTryAgainButton
  />
);

export const InterviewScreenSharingRequired = () => (
  <InterviewStatusPage
    icon={
      <ScreenShareIcon
        sx={{
          width: 64,
          height: 64,
          color: 'error.main',
        }}
      />
    }
    title='Screen sharing required'
    description='Screen sharing is mandatory for this live coding assessment. Please refresh the page and ensure screen sharing is enabled before proceeding.'
    showTryAgainButton
  />
);

export const InterviewErrorLoading = () => (
  <InterviewStatusPage
    icon={
      <ErrorOutlineIcon
        sx={{
          width: 64,
          height: 64,
          color: 'error.main',
        }}
      />
    }
    title='Error loading interview'
    description='We encountered an error while loading your interview. Please try again.'
    showTryAgainButton
  />
);

export const InterviewDeviceAccessError = () => (
  <InterviewStatusPage
    icon={
      <ErrorOutlineIcon
        sx={{
          width: 64,
          height: 64,
          color: 'error.main',
        }}
      />
    }
    title='Error loading media devices'
    description='We encountered an error while accessing your microphone or camera. Please try again.'
    showTryAgainButton
  />
);

export const InterviewLoading = () => (
  <Box
    sx={{
      opacity: 0,
      animation: 'fadeIn 1s ease-in forwards',
      animationDelay: '2s',
      '@keyframes fadeIn': {
        to: {
          opacity: 1,
        },
      },
    }}
  >
    <InterviewStatusPage
      icon={<CircularProgress />}
      title='Loading interview'
      description='Please wait while we prepare your interview...'
    />
  </Box>
);

export const InterviewCompleting = () => (
  <Box
    sx={{
      opacity: 0,
      animation: 'fadeIn 1s ease-in forwards',
      animationDelay: '2s',
      '@keyframes fadeIn': {
        to: {
          opacity: 1,
        },
      },
    }}
  >
    <InterviewStatusPage
      icon={<CircularProgress />}
      title='Completing interview'
      description='Please wait while we complete your interview...'
    />
  </Box>
);

export const InterviewNotFound = () => (
  <InterviewStatusPage
    icon={
      <motion.div
        animate={{ rotate: [0, 10, -10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        style={{ display: 'inline-block' }}
      >
        <SentimentDissatisfiedIcon
          sx={{
            fontSize: 64,
            color: 'warning.main',
          }}
        />
      </motion.div>
    }
    title='Interview not found'
    description="The interview you're looking for doesn't exist or has been removed."
  />
);

export const InterviewResumeError = () => (
  <InterviewStatusPage
    icon={
      <ErrorOutlineIcon
        sx={{
          width: 64,
          height: 64,
          color: 'error.main',
        }}
      />
    }
    title='Error loading resume'
    description='We encountered an error while loading your resume.'
  />
);

export const InterviewZaraBusy = () => (
  <Box
    display='flex'
    alignItems='center'
    justifyContent='center'
    minHeight='100vh'
    position='relative'
  >
    <Box
      position='absolute'
      top={0}
      left={0}
      right={0}
      bottom={0}
      sx={{
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? 'radial-gradient(circle at 50% 50%, #1a1a1a 0%, #000000 100%)'
            : 'radial-gradient(circle at 50% 50%, #ffffff 0%, #f5f5f5 100%)',
      }}
    />

    {/* Content Container */}
    <Box
      textAlign='center'
      maxWidth='600px'
      mx='auto'
      px={3}
      sx={{
        animation: 'fadeIn 0.5s ease-out',
        '@keyframes fadeIn': {
          from: { opacity: 0, transform: 'translateY(20px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      {/* Zara Avatar Container */}
      <Box
        height='50px'
        position='relative'
        mb={1}
        sx={{
          '& > div': {
            position: 'relative',
            height: '50px',
          },
        }}
      >
        <ZaraAvatar isAiSpeaking={false} isLoading={true} />
      </Box>

      <Box sx={{ position: 'relative' }}>
        <Typography
          variant='h4'
          sx={{
            fontSize: '1.75rem',
            mb: 2,
            color: (theme) => theme.palette.text.primary,
          }}
        >
          Zara is temporarily unavailable
        </Typography>

        <Typography
          variant='body1'
          sx={{
            mb: 1,
            maxWidth: '460px',
            mx: 'auto',
            color: (theme) => theme.palette.text.secondary,
          }}
        >
          Please try again in a few minutes.
        </Typography>
      </Box>

      <Button
        onClick={() => window.location.reload()}
        variant='contained'
        size='large'
        color='primary'
        sx={{
          px: 4,
          py: 1,
          borderRadius: 2,
          textTransform: 'none',
          fontSize: '1rem',
          boxShadow: (theme) => `0 4px 14px ${theme.palette.primary.main}25`,
          '&:hover': {
            boxShadow: (theme) => `0 6px 20px ${theme.palette.primary.main}40`,
          },
        }}
      >
        Try again
      </Button>
    </Box>
  </Box>
);
